'use client'

import { AvatarFallbackProps, AvatarRootProps, Avatar as ChakraAvatar } from '@chakra-ui/react/avatar'
import { Group, GroupProps } from '@chakra-ui/react/group'
import * as React from 'react'

type AvatarProps = AvatarRootProps & {
  name?: string
  src?: string
  srcSet?: string
  loading?: 'eager' | 'lazy'
  icon?: React.ReactNode
  fallback?: React.ReactNode
}

export const Avatar = React.forwardRef(function Avatar(props: AvatarProps, ref: React.Ref<HTMLDivElement>) {
  const { name, src, srcSet, loading, icon, fallback, children, ...rest } = props
  return (
    <ChakraAvatar.Root ref={ref} {...rest}>
      <AvatarFallback name={name} icon={icon}>
        {fallback}
      </AvatarFallback>
      <ChakraAvatar.Image src={src} srcSet={srcSet} loading={loading} />
      {children}
    </ChakraAvatar.Root>
  )
})

const AvatarFallback = React.forwardRef(function AvatarFallback(
  props: AvatarFallbackProps & {
    name?: string
    icon?: React.ReactNode
  },
  ref: React.Ref<HTMLDivElement>,
) {
  const { name, icon, children, ...rest } = props
  return (
    <ChakraAvatar.Fallback ref={ref} {...rest}>
      {children}
      {name != null && children == null && <>{getInitials(name)}</>}
      {name == null && children == null && <ChakraAvatar.Icon asChild={!!icon}>{icon}</ChakraAvatar.Icon>}
    </ChakraAvatar.Fallback>
  )
})

export function getInitials(name: string) {
  const names = name.trim().split(' ')
  const firstName = names[0] != null ? names[0] : ''
  const lastName = names.length > 1 ? names[names.length - 1] : ''
  return firstName && lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : firstName.charAt(0)
}

export const AvatarGroup = React.forwardRef(function AvatarGroup(
  props: GroupProps & { size?: 'sm' | 'md' | 'lg'; variant?: 'outline' | 'solid' | 'subtle'; borderless?: boolean },
  ref: React.Ref<HTMLDivElement>,
) {
  const { size, variant, borderless, ...rest } = props
  return (
    <ChakraAvatar.PropsProvider value={{ size, variant, borderless }}>
      <Group gap="0" spaceX="-3" ref={ref} {...rest} />
    </ChakraAvatar.PropsProvider>
  )
})
